import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ShieldCheck, Loader2 } from "lucide-react";
import { useAuth } from "../../lib/AuthContext";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useBrand } from "../../lib/BrandContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import PasswordInput from "../../components/PasswordInput";
import { notifyInstallMilestone } from "../../lib/installMilestone";

export default function AcceptInvite() {
  const { code } = useParams();
  const { acceptInvite } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/invites/${code}`)
      .then((r) => {
        setInvite(r.data);
        // Email may be null on a link-only invite — only prefill if present
        // so the parent can type in their own address on submit.
        if (r.data?.invite?.email) {
          setForm((p) => ({ ...p, email: r.data.invite.email }));
        }
      })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [code]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      await acceptInvite({ code, ...form });
      // Stash a one-time welcome payload for the parent's first Home visit.
      // Cleared by the welcome dialog "Got it" handler. Keys deliberately
      // scoped to "sf_welcome_parent_v1" so future iterations can rev this.
      try {
        const payload = {
          coach_name: invite?.coach?.name || "",
          org_name: invite?.org?.name || "",
          team_name: invite?.team?.name || "",
          v: 1,
          set_at: new Date().toISOString(),
        };
        localStorage.setItem("sf_welcome_parent_v1", JSON.stringify(payload));
      } catch { /* ignore localStorage failures (private mode, quota) */ }
      // Milestone — parent joined a team.
      notifyInstallMilestone();
      navigate("/parent/dashboard");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="flex min-h-screen bg-ink-100/40">
      <div className="m-auto w-full max-w-md px-6 py-10">
        <Link to="/" className="flex items-center gap-2 font-heading text-xl font-bold text-ink-900">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand text-white"><ShieldCheck className="h-5 w-5" /></span>
          {brand.app_name}
        </Link>
        <div className="mt-8 rounded-2xl border border-ink-100 bg-white p-8 shadow-sm">
          {loading ? (
            <div className="flex items-center gap-2 text-ink-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading invite…</div>
          ) : !invite ? (
            <div>
              <h1 className="font-heading text-2xl font-bold text-ink-900">Invite not found</h1>
              <p className="mt-2 text-sm text-ink-500">{error || "This invite may be expired or already used."}</p>
            </div>
          ) : (
            <>
              <p className="label-caps text-brand">You&apos;ve been invited</p>
              <h1 className="mt-2 font-heading text-2xl font-bold text-ink-900">
                Join {invite.team?.name}
              </h1>

              {/* Trust line — clearly name the coach + team that invited the parent.
                  Only prepend "Coach" when the stored name doesn't already start
                  with that title (avoids "Coach Coach Maria Ramirez"). */}
              {(invite.coach?.name || invite.org?.name) ? (
                <p className="mt-3 text-sm text-ink-700" data-testid="invite-trust-line">
                  You were invited by{" "}
                  {invite.coach?.name
                    ? <span className="font-semibold text-ink-900">
                        {/^(Coach|Trainer|Director)\b/i.test(invite.coach.name) ? invite.coach.name : `Coach ${invite.coach.name}`}
                      </span>
                    : <span className="font-semibold text-ink-900">your coach</span>}
                  {invite.org?.name ? (
                    <> from <span className="font-semibold text-ink-900">{invite.org.name}</span></>
                  ) : null}
                  .
                </p>
              ) : null}

              {invite.player ? (
                <p className="mt-1 text-sm text-ink-500">
                  This account will be linked to{" "}
                  <span className="font-semibold text-ink-700">
                    {invite.player.first_name} {invite.player.last_name}
                  </span>
                  .
                </p>
              ) : null}

              {/* Off-platform disclosure — keeps the parent oriented before they sign up. */}
              <div className="mt-4 rounded-xl border border-ink-100 bg-ink-100/40 p-3" data-testid="invite-off-platform-note">
                <p className="text-xs text-ink-700">
                  {brand.app_name} helps your coach track payments, but payments are sent
                  outside the app using your coach&apos;s instructions
                  (Venmo, Zelle, Cash App, cash, or check).
                </p>
              </div>

              <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="accept-invite-form">
                <div>
                  <Label htmlFor="name">Your name</Label>
                  <Input id="name" required value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} className="mt-1 h-12" data-testid="accept-name-input" />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" required value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} className="mt-1 h-12" data-testid="accept-email-input" />
                </div>
                <div>
                  <Label htmlFor="password">Create password</Label>
                  <PasswordInput id="password" required minLength={6} autoComplete="new-password" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} className="mt-1 h-12" data-testid="accept-password-input" capsWarningTestId="accept-caps-warning" />
                </div>
                {error ? <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p> : null}
                <Button type="submit" disabled={submitting} className="h-12 w-full bg-brand font-semibold text-white hover:bg-brand-hover" data-testid="accept-submit-button">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accept invite"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
